import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI, refreshTier } from '../services/api';
import { API } from '../config';
import RecruiterSidebar from '../components/RecruiterSidebar';
import AppTour from '../components/AppTour';
import { RECRUITER_TOUR_STEPS } from '../data/tourSteps';
import '../styles/theme.css';
import './RecruiterDashboard.css';

function ConfirmModal({ title, message, confirmLabel = 'Delete', onConfirm, onCancel }) {
  return (
    <div className="rd-modal-overlay" onClick={onCancel}>
      <div className="rd-modal" onClick={e => e.stopPropagation()}>
        <div className="rd-modal-icon"><i className="fas fa-trash-alt"></i></div>
        <h3 className="rd-modal-title">{title}</h3>
        <p className="rd-modal-msg">{message}</p>
        <div className="rd-modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function RecruiterDashboard() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    interviewsScheduled: 0,
    hiredCandidates: 0,
  });

  useEffect(() => {
    refreshTier();
    loadDashboardData();
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(loadDashboardData, 30000);

    // App tour: show once for new recruiters
    const tourDone = localStorage.getItem('recruiter_tour_done') === 'true';
    if (!tourDone) setShowTour(true);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const jobsResponse = await recruiterAPI.getMyJobs();
      const jobsData = jobsResponse.data;
      setJobs(jobsData);

      try {
        const profileResponse = await recruiterAPI.getProfile();
        setProfile(profileResponse.data);
        if (profileResponse.data?.company_name) {
          localStorage.setItem('company_name', profileResponse.data.company_name);
        }
      } catch {}

      // Fetch real application counts for each job
      let total = 0, pending = 0, interviews = 0, hired = 0;
      await Promise.all(jobsData.map(async (job) => {
        try {
          const res = await fetch(`${API}/applications/job/${job.id}/applications/`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const apps = await res.json();
            total += apps.length;
            pending += apps.filter(a => ['pending','applied','viewed'].includes(a.status?.toLowerCase())).length;
            interviews += apps.filter(a => a.status?.toLowerCase() === 'interview').length;
            hired += apps.filter(a => a.status?.toLowerCase() === 'hired').length;
          }
        } catch {}
      }));

      setAnalytics({ totalApplications: total, pendingApplications: pending, interviewsScheduled: interviews, hiredCandidates: hired });
    } catch (err) {
      if (err.response?.status === 401) navigate('/recruiter/login');
      else setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await recruiterAPI.deleteJob(jobId);
      const updated = jobs.filter(j => j.id !== jobId);
      setJobs(updated);
      calculateAnalytics(updated);
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
      setError('Failed to delete job. Please try again.');
    }
  };

  const handleToggleActive = async (job) => {
    try {
      await recruiterAPI.updateJob(job.id, { is_active: !job.is_active });
      setJobs(jobs.map(j => j.id === job.id ? { ...j, is_active: !j.is_active } : j));
    } catch { setError('Failed to update job status.'); }
  };

  const handleToggleFeatured = async (job) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/jobs/${job.id}/feature/`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to update featured status.'); return; }
      setJobs(jobs.map(j => j.id === job.id ? { ...j, is_featured: data.is_featured } : j));
    } catch { setError('Failed to update featured status.'); }
  };

  const activeJobs = jobs.filter(j => j.is_active);
  const inactiveJobs = jobs.filter(j => !j.is_active);
  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Recently';

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Recruiter Dashboard</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme"><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/recruiter/post-job')}>
              <i className="fas fa-plus"></i> <span className="topbar-btn-label">Post Job</span>
            </button>
          </div>
        </div>

        <div className="portal-content">
          {/* Welcome */}
          <div className="rd-welcome card">
            {/* Animated background blobs */}
            <div className="rd-welcome-bg">
              <div className="rd-blob rd-blob-1" />
              <div className="rd-blob rd-blob-2" />
              <div className="rd-blob rd-blob-3" />
            </div>

            <div className="rd-welcome-content">
              <p className="rd-welcome-sub">Welcome back</p>
              <h2 className="rd-welcome-title">
                {profile?.company_name
                  ? <span className="rd-animated-name">{profile.company_name}</span>
                  : 'Your Dashboard'
                }
              </h2>
              <p className="rd-welcome-desc">Here's what's happening with your recruitment</p>
            </div>

            <div className="rd-welcome-meta">
              <span><i className="fas fa-calendar-alt"></i> Member since {memberSince}</span>
              <span><i className="fas fa-crown"></i> {
                { starter: 'Starter', professional: 'Professional', business: 'Business' }[localStorage.getItem('user_tier')] || profile?.current_plan || 'Starter'
              } Plan</span>
            </div>
          </div>

          {/* Analytics */}
          <div className="stats-row" style={{ marginTop: '1.5rem' }}>
            <div className="stat-card">
              <div className="stat-icon-wrap blue"><i className="fas fa-file-alt"></i></div>
              <div className="stat-info">
                <div className="stat-value">{analytics.totalApplications}</div>
                <div className="stat-label">Total Applications</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap orange"><i className="fas fa-clock"></i></div>
              <div className="stat-info">
                <div className="stat-value">{analytics.pendingApplications}</div>
                <div className="stat-label">Pending Review</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap green"><i className="fas fa-calendar-check"></i></div>
              <div className="stat-info">
                <div className="stat-value">{analytics.interviewsScheduled}</div>
                <div className="stat-label">Interviews Scheduled</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap purple"><i className="fas fa-user-check"></i></div>
              <div className="stat-info">
                <div className="stat-value">{analytics.hiredCandidates}</div>
                <div className="stat-label">Hired</div>
              </div>
            </div>
          </div>

          {/* Job Stats */}
          <div className="card" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title">Job Postings Overview</span>
            </div>
            <div className="rd-job-stats">
              <div className="rd-stat-box">
                <span className="rd-stat-num">{activeJobs.length}</span>
                <span className="rd-stat-lbl">Active Jobs</span>
              </div>
              <div className="rd-stat-box">
                <span className="rd-stat-num">{inactiveJobs.length}</span>
                <span className="rd-stat-lbl">Inactive Jobs</span>
              </div>
              <div className="rd-stat-box">
                <span className="rd-stat-num">{jobs.length}</span>
                <span className="rd-stat-lbl">Total Jobs</span>
              </div>
            </div>
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i><p>Loading...</p></div>
          ) : error ? (
            <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i>{error}</div>
          ) : jobs.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-briefcase"></i>
              <h3>No jobs posted yet</h3>
              <p>Start by posting your first job opening</p>
              <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/recruiter/post-job')}>
                Post Your First Job
              </button>
            </div>
          ) : (
            <div className="rd-jobs-list">
              {jobs.map(job => (
                <div key={job.id} className={`card rd-job-card ${!job.is_active ? 'rd-inactive' : ''}`}>
                  <div className="rd-job-header">
                    <div>
                      <h3 className="rd-job-title">{job.title}</h3>
                      <p className="rd-job-company">{job.company}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {job.is_featured && (
                        <span className="badge" style={{ background: '#f59e0b', color: '#fff' }}>
                          <i className="fas fa-star"></i> Featured
                        </span>
                      )}
                      <span className={`badge ${job.is_active ? 'badge-green' : 'badge-red'}`}>
                        {job.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="rd-job-info">
                    <span><i className="fas fa-briefcase"></i> {job.employment_type || 'Full-time'}</span>
                    <span><i className="fas fa-map-marker-alt"></i> {job.city && job.country ? `${job.city}, ${job.country}` : job.location || 'Not specified'}</span>
                    <span><i className="fas fa-laptop-house"></i> {job.work_mode || 'Remote'}</span>
                    {job.salary && <span><i className="fas fa-money-bill-wave"></i> {job.salary}</span>}
                  </div>

                  <div className="rd-job-meta">
                    <span>Posted: {new Date(job.posted_date).toLocaleDateString()}</span>
                    {job.application_deadline && <span>Deadline: {new Date(job.application_deadline).toLocaleDateString()}</span>}
                  </div>

                  <div className="rd-job-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/recruiter/jobs/${job.id}/applications`)}>
                      <i className="fas fa-users"></i> Applications
                    </button>
                    <button
                      className={`btn btn-sm ${job.is_featured ? 'btn-warning' : 'btn-secondary'}`}
                      onClick={() => handleToggleFeatured(job)}
                      title={job.is_featured ? 'Remove featured' : 'Mark as featured'}
                      style={job.is_featured ? { background: '#f59e0b', color: '#fff', borderColor: '#f59e0b' } : {}}
                    >
                      <i className={`fas fa-star`}></i> {job.is_featured ? 'Featured' : 'Feature'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleToggleActive(job)}>
                      {job.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/recruiter/edit-job/${job.id}`)}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget({ id: job.id, title: job.title })}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Job Posting"
          message={`Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => handleDeleteJob(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* App Tour — shown once for new recruiters */}
      {showTour && (
        <AppTour
          steps={RECRUITER_TOUR_STEPS}
          storageKey="recruiter_tour_done"
          onEnd={() => setShowTour(false)}
        />
      )}
    </div>
  );
}

export default RecruiterDashboard;
